/**
 * البوابة المحلية: تنفيذ كامل لعقد DataGateway فوق IndexedDB.
 *
 * كل استدعاء يمرّ عبر سياسة الوصول الحالية (PolicyContext) المحسوبة من
 * الجلسة والعضويات. عند تغيير الشركة النشطة أو تعديل الأدوار أو تسجيل
 * الدخول/الخروج، يُعاد تحميل السياق عبر refresh().
 */

import { AppError } from '@/domain/errors';
import { newId } from '@/domain/ids';
import type { PolicyContext } from '../policy';
import type {
  DataGateway,
  GatewayCommands,
  QueryOptions,
  Repository,
  Row,
  TableName,
} from '../types';
import { LocalRepository } from './repository';
import { EMPTY_POLICY_CONTEXT, loadSessionState, setActiveCompany } from './context';
import { LocalBlobStore } from './blob-store-local';
import { LocalAuthGateway } from './auth';
import { createCommands, type LocalCore } from './commands';
import { clearAllLocalData, getLocalDb } from './db';
import type { RowReadHint } from '../policy';
import { redactForAudit, severityForAction } from '@/domain/audit';

export class LocalGateway implements DataGateway {
  readonly kind = 'local' as const;
  readonly supportsOffline = true;
  readonly auth = new LocalAuthGateway();
  readonly commands: GatewayCommands;

  private policyContext: PolicyContext = EMPTY_POLICY_CONTEXT;
  private readonly blob = new LocalBlobStore();
  private readonly repositories = new Map<TableName, Repository<Row<TableName>>>();
  /** هل حُمّل سياق السياسة من الجلسة؟ لا تُقرأ الجداول قبل ذلك. */
  private contextLoaded = false;
  /** يمنع كتابة نتائج تحديث قديم فوق تحديث أحدث */
  private refreshSeq = 0;
  private serial: Promise<unknown> = Promise.resolve();

  constructor() {
    this.commands = createCommands(this.core());
    // عند تغيّر حالة المصادقة (دخول/خروج/انتهاء جلسة) يصبح السياق قديمًا:
    // نبطله فورًا ونُبطل أي تحديث جارٍ حتى لا يُكتب سياق قديم فوق الجديد.
    this.auth.onAuthStateChange(() => {
      this.refreshSeq += 1;
      this.contextLoaded = false;
    });
    void this.refresh();
  }

  /** يُنتظر قبل أي عملية لضمان أن سياق السياسة محمّل (وإلا رُفضت كل قراءة) */
  ready(): Promise<void> {
    return this.ensureContext();
  }

  async refresh(): Promise<void> {
    const seq = ++this.refreshSeq;
    const state = await loadSessionState();
    // تجاهل نتيجة تحديث أقدم إن بدأ تحديث أحدث أثناء انتظارنا
    if (seq !== this.refreshSeq) return;
    this.policyContext = state?.policy ?? EMPTY_POLICY_CONTEXT;
    this.contextLoaded = true;
  }

  /** يعيد تحميل السياق فقط إذا لم يُحمّل بعد، لتفادي تكرار العمل. */
  private async ensureContext(): Promise<void> {
    if (this.contextLoaded) return;
    await this.refresh();
  }

  setCompany(companyId: string | null): void {
    setActiveCompany(companyId);
  }

  async setActiveCompany(companyId: string | null): Promise<void> {
    setActiveCompany(companyId);
    await this.refresh();
  }

  policy(): PolicyContext {
    return this.policyContext;
  }

  table<T extends TableName>(name: T): Repository<Row<T>> {
    const existing = this.repositories.get(name);
    if (existing) return existing as Repository<Row<T>>;
    const repository = new LocalRepository(name, {
      getPolicyContext: () => this.policyContext,
      ensureReady: () => this.ensureContext(),
      resolveHint: (table, row) => this.resolveHint(table, row),
      beforeInsert: async (table, row) => this.beforeInsert(table, row),
      beforeUpdate: async (table, row, patch) => this.beforeUpdate(table, row, patch),
    });
    this.repositories.set(name, repository as unknown as Repository<Row<TableName>>);
    return repository as unknown as Repository<Row<T>>;
  }

  supportsOfflineMode(): boolean {
    return true;
  }

  /**
   * في الوضع المحلي لا يوجد تراجع (rollback) عبر استدعاءات متعددة:
   * نُسلسل التنفيذ فقط. العمليات التي تحتاج ذرّية تُنفَّذ داخل الأوامر
   * بمعاملة IndexedDB واحدة.
   */
  async transaction<T>(work: () => Promise<T>): Promise<T> {
    const run = this.serial.then(work, work);
    this.serial = run.catch(() => undefined);
    return run;
  }

  async resetLocalState(): Promise<void> {
    await clearAllLocalData();
    this.policyContext = EMPTY_POLICY_CONTEXT;
    this.repositories.clear();
    this.blob.dispose();
  }

  /* ----------------------------- سياق القراءة ----------------------------- */

  /** نطاق العضو الهدف لمطابقة عزل الموظفين مع scope_covers_membership في SQL. */
  private async membershipScopeHint(
    membershipId: string,
  ): Promise<Pick<RowReadHint, 'membershipUserId' | 'membershipBranchIds' | 'membershipBrandIds'>> {
    const membership = await this.rawGet('memberships', membershipId);
    if (!membership) return { membershipUserId: null, membershipBranchIds: [], membershipBrandIds: [] };
    const branchIds = new Set<string>();
    const brandIds = new Set<string>();
    if (membership.primaryBranchId) branchIds.add(membership.primaryBranchId);
    const placements = (await this.rawFind('membership_placements', 'membershipId', membershipId)).filter(
      (placement) => !placement.deletedAt,
    );
    for (const placement of placements) {
      if (placement.scopeType === 'branch') branchIds.add(placement.scopeId);
      else brandIds.add(placement.scopeId);
    }
    for (const branchId of branchIds) {
      const branch = await this.rawGet('branches', branchId);
      if (branch?.brandId) brandIds.add(branch.brandId);
    }
    return {
      membershipUserId: membership.userId,
      membershipBranchIds: [...branchIds],
      membershipBrandIds: [...brandIds],
    };
  }

  private async resolveHint(table: TableName, row: Record<string, unknown>): Promise<RowReadHint | undefined> {
    switch (table) {
      case 'memberships': {
        const membershipId = row['id'] as string | undefined;
        if (!membershipId) return undefined;
        return {
          parentCompanyId: (row['companyId'] as string | undefined) ?? null,
          ...(await this.membershipScopeHint(membershipId)),
        };
      }
      case 'membership_placements': {
        const membershipId = row['membershipId'] as string | undefined;
        if (!membershipId) return undefined;
        return {
          parentCompanyId: (row['companyId'] as string | undefined) ?? null,
          ...(await this.membershipScopeHint(membershipId)),
        };
      }
      case 'profiles': {
        const userId = row['id'] as string | undefined;
        if (!userId) return undefined;
        if (userId === this.policyContext.userId) return undefined;
        const memberships = await this.rawFind('memberships', 'userId', userId);
        const shared = memberships.find((m) => m.companyId === this.policyContext.companyId);
        return { parentCompanyId: shared?.companyId ?? memberships[0]?.companyId ?? null };
      }
      case 'role_permissions':
      case 'role_scopes': {
        const roleId = row['roleId'] as string | undefined;
        if (!roleId) return { parentCompanyId: null };
        const role = await this.rawGet('roles', roleId);
        return { parentCompanyId: role?.companyId ?? null };
      }
      case 'settings': {
        const scopeType = row['scopeType'] as string | undefined;
        const scopeId = row['scopeId'] as string | undefined;
        if (scopeType === 'platform') return { parentCompanyId: null };
        if (scopeType === 'company') return { parentCompanyId: scopeId ?? null };
        if (scopeType === 'brand' && scopeId) {
          const brand = await this.rawGet('brands', scopeId);
          return { parentCompanyId: brand?.companyId ?? null };
        }
        if (scopeType === 'branch' && scopeId) {
          const branch = await this.rawGet('branches', scopeId);
          return { parentCompanyId: branch?.companyId ?? null };
        }
        if (scopeType === 'user' && scopeId) {
          const memberships = await this.rawFind('memberships', 'userId', scopeId);
          const shared = memberships.find((m) => m.companyId === this.policyContext.companyId);
          return { parentCompanyId: shared?.companyId ?? memberships[0]?.companyId ?? null };
        }
        return undefined;
      }
      case 'membership_roles': {
        const membershipId = row['membershipId'] as string | undefined;
        if (!membershipId) return { parentCompanyId: null };
        const membership = await this.rawGet('memberships', membershipId);
        return { parentCompanyId: membership?.companyId ?? null };
      }
      case 'task_template_assignees': {
        const templateId = row['templateId'] as string | undefined;
        if (!templateId) return { parentCompanyId: null };
        const template = await this.rawGet('task_templates', templateId);
        return { parentCompanyId: template?.companyId ?? null };
      }
      case 'tasks': {
        const taskId = row['id'] as string | undefined;
        if (!taskId) return undefined;
        const assignees = await this.rawFind('task_assignees', 'taskId', taskId);
        return {
          assigneeMembershipIds: assignees.filter((a) => !a.unassignedAt).map((a) => a.membershipId),
          completerMembershipIds: assignees
            .filter((a) => a.state === 'completed')
            .map((a) => a.membershipId),
        };
      }
      case 'task_assignees':
      case 'task_steps':
      case 'task_status_history':
      case 'attachments': {
        const taskId = row['taskId'] as string | undefined;
        if (!taskId) {
          return { parentCompanyId: (row['companyId'] as string | undefined) ?? null };
        }
        const task = await this.rawGet('tasks', taskId);
        if (!task) return { parentTask: null };
        const assignees = await this.rawFind('task_assignees', 'taskId', taskId);
        return {
          parentTask: task,
          parentCompanyId: task.companyId,
          assigneeMembershipIds: assignees.filter((a) => !a.unassignedAt).map((a) => a.membershipId),
          completerMembershipIds: assignees
            .filter((a) => a.state === 'completed')
            .map((a) => a.membershipId),
        };
      }
      default:
        return undefined;
    }
  }

  /* -------------------------- تحقق إضافي قبل الكتابة ----------------------- */

  private async beforeInsert(table: TableName, row: Record<string, unknown>): Promise<void> {
    const ctx = this.policyContext;
    if (!ctx.userId) throw AppError.unauthenticated();

    // فرض معرّف الشركة من السياق الموثوق عند الإدراج
    const tenantScoped: TableName[] = [
      'brands',
      'branches',
      'brand_branch_links',
      'task_categories',
      'memberships',
      'membership_access_grants',
      'supervision_relations',
      'task_templates',
      'tasks',
      'attachments',
      'attachment_deletions',
      'notifications',
      'company_modules',
    ];
    if (tenantScoped.includes(table) && row['companyId'] !== ctx.companyId) {
      throw AppError.forbidden('لا يمكن إنشاء سجل في منشأة أخرى');
    }

    await this.verifyReferences(table, row, true);
  }

  private async beforeUpdate(
    table: TableName,
    row: Record<string, unknown>,
    patch: Record<string, unknown>,
  ): Promise<void> {
    // منع تغيير ملكية السجل بين الشركات بتحديث companyId
    if ('companyId' in patch && patch['companyId'] !== row['companyId']) {
      throw AppError.forbidden('لا يمكن نقل سجل إلى منشأة أخرى');
    }
    if (table === 'tasks' && 'status' in patch) {
      // تغيير الحالة يجب أن يمر عبر الأوامر الخادمية لا عبر تحديث مباشر
      throw AppError.forbidden('تغيير حالة المهمة يتم عبر أوامر الإنجاز والاعتماد فقط');
    }
    if (
      (table === 'tasks' || table === 'task_templates') &&
      'createdByMembershipId' in patch &&
      patch['createdByMembershipId'] !== row['createdByMembershipId']
    ) {
      throw AppError.forbidden('لا يمكن تغيير منشئ المهمة أو القالب');
    }
    await this.verifyReferences(table, { ...row, ...patch }, false);
  }

  /**
   * تحقق من المراجع بين الجداول: منع ربط سجل في شركتك بسجل تابع لشركة أخرى
   * حتى لو كان معرّفه معروفًا. لا تمنع هذه الدالة مالك المنصة (سياق موثوق).
   */
  private async verifyReferences(
    table: TableName,
    row: Record<string, unknown>,
    isInsert: boolean,
  ): Promise<void> {
    const ctx = this.policyContext;
    const trusted = ctx.isPlatformOwner;

    const ref = async (name: TableName, id: unknown, label: string): Promise<Record<string, unknown> | null> => {
      if (typeof id !== 'string' || id.length === 0) return null;
      const found = (await this.rawGet(name, id)) as unknown as Record<string, unknown> | null;
      if (!found) throw AppError.validation(`${label} غير موجود`);
      const tenantId =
        name === 'companies'
          ? (found['id'] as string)
          : ((found['companyId'] as string | undefined) ?? null);
      if (!trusted && tenantId && tenantId !== ctx.companyId) {
        throw AppError.forbidden(`${label} تابع لمنشأة أخرى`);
      }
      return found;
    };

    const requireOwnCreator = (): void => {
      if (trusted) return;
      if (row['createdByMembershipId'] !== ctx.membershipId) {
        throw AppError.forbidden('لا يمكن إنشاء سجل باسم عضوية أخرى');
      }
    };

    switch (table) {
      case 'profiles': {
        if (trusted) break;
        const targetId = row['id'];
        if (typeof targetId !== 'string' || targetId === ctx.userId) break;
        const memberships = await this.rawFind('memberships', 'userId', targetId);
        if (!memberships.some((m) => m.companyId === ctx.companyId && m.status !== 'ended')) {
          throw AppError.forbidden('لا يمكن تعديل ملف مستخدم خارج شركتك');
        }
        break;
      }
      case 'memberships': {
        await ref('branches', row['primaryBranchId'], 'الفرع');
        if (row['isCompanyOwner'] === true && !trusted) {
          const actorIsOwner = ctx.access.membership?.membership.isCompanyOwner === true;
          if (!actorIsOwner) {
            throw AppError.forbidden('تعيين مسؤول المنشأة متاح لمسؤول المنشأة الحالي فقط');
          }
        }
        if (isInsert) {
          const sameCompany = (await this.rawFind('memberships', 'companyId', ctx.companyId ?? '')).filter(
            (m) => m.userId === row['userId'] && m.status !== 'ended',
          );
          if (sameCompany.length > 0) {
            throw AppError.conflict('هذا المستخدم عضو بالفعل في المنشأة');
          }
        }
        break;
      }
      case 'membership_roles': {
        const membership = await ref('memberships', row['membershipId'], 'العضوية');
        const role = await ref('roles', row['roleId'], 'الدور');
        if (!trusted) {
          if (!membership) throw AppError.validation('العضوية مطلوبة');
          if (!role || role['companyId'] !== ctx.companyId || role['isPlatformRole'] === true) {
            throw AppError.forbidden('لا يمكن إسناد دور من منشأة أو مستوى آخر');
          }
        }
        break;
      }
      case 'role_permissions':
      case 'role_scopes': {
        const role = await ref('roles', row['roleId'], 'الدور');
        if (!trusted && (!role || role['companyId'] !== ctx.companyId)) {
          throw AppError.forbidden('لا يمكن تعديل صلاحيات دور خارج شركتك');
        }
        if (table === 'role_scopes' && row['scopeType'] === 'branch') {
          await ref('branches', row['targetId'], 'الفرع');
        }
        if (table === 'role_scopes' && row['scopeType'] === 'brand') {
          await ref('brands', row['targetId'], 'العلامة');
        }
        break;
      }
      case 'membership_access_grants': {
        await ref('memberships', row['membershipId'], 'العضوية');
        if (row['scopeType'] === 'branch') await ref('branches', row['targetId'], 'الفرع');
        if (row['scopeType'] === 'brand') await ref('brands', row['targetId'], 'العلامة');
        break;
      }
      case 'supervision_relations': {
        await ref('memberships', row['supervisorMembershipId'], 'المشرف');
        await ref('memberships', row['subordinateMembershipId'], 'المرؤوس');
        break;
      }
      case 'brand_branch_links': {
        await ref('brands', row['brandId'], 'العلامة');
        await ref('branches', row['branchId'], 'الفرع');
        break;
      }
      case 'settings': {
        const scopeType = row['scopeType'] as string | undefined;
        const scopeId = row['scopeId'] as string | undefined;
        if (scopeType === 'platform') {
          if (!trusted) throw AppError.forbidden('إعدادات المنصة متاحة لمالك المنصة فقط');
          break;
        }
        if (scopeType === 'company') {
          if (scopeId !== ctx.companyId) throw AppError.forbidden('لا يمكن تعديل إعدادات منشأة أخرى');
          break;
        }
        if (scopeType === 'brand') await ref('brands', scopeId, 'العلامة');
        if (scopeType === 'branch') await ref('branches', scopeId, 'الفرع');
        if (scopeType === 'user') await ref('memberships', scopeId, 'المستخدم');
        break;
      }
      case 'task_categories':
      case 'brands':
      case 'branches':
      case 'company_modules':
      case 'attachment_deletions':
      case 'notifications':
        break;
      case 'tasks':
      case 'task_templates': {
        requireOwnCreator();
        await ref('task_categories', row['categoryId'], 'فئة المهمة');
        await ref('brands', row['brandId'], 'العلامة');
        await ref('branches', row['branchId'], 'الفرع');
        await ref('memberships', row['approverMembershipId'], 'المعتمد');
        if (table === 'tasks') {
          const scope = row['assigneeScope'] as { membershipIds?: string[] } | undefined;
          for (const membershipId of scope?.membershipIds ?? []) {
            await ref('memberships', membershipId, 'العضوية المسندة');
          }
        }
        break;
      }
      case 'task_template_assignees': {
        if (!trusted) {
          const template = await ref('task_templates', row['templateId'], 'القالب');
          if (!template || template['companyId'] !== ctx.companyId) {
            throw AppError.forbidden('القالب تابع لمنشأة أخرى');
          }
        }
        await ref('memberships', row['membershipId'], 'العضوية');
        break;
      }
      case 'task_assignees':
      case 'task_steps':
      case 'attachments': {
        const task = await ref('tasks', row['taskId'], 'المهمة');
        if (table === 'task_assignees') {
          await ref('memberships', row['membershipId'], 'العضوية');
        }
        if (table === 'attachments' && task && row['companyId'] !== task['companyId']) {
          throw AppError.validation('المرفق يجب أن يتبع منشأة المهمة');
        }
        break;
      }
      default:
        break;
    }
  }

  /* ------------------------------ وصول الداخل ----------------------------- */

  async rawGet<T extends TableName>(name: T, id: string): Promise<Row<T> | null> {
    const db = await getLocalDb();
    const row = await db.get(name, id);
    return (row as Row<T> | undefined) ?? null;
  }

  async rawFind<T extends TableName>(name: T, field: string, value: unknown): Promise<Row<T>[]> {
    const db = await getLocalDb();
    const rows = (await db.getAll(name)) as unknown as Row<T>[];
    return rows.filter((row) => (row as unknown as Record<string, unknown>)[field] === value);
  }

  async rawAll<T extends TableName>(name: T): Promise<Row<T>[]> {
    const db = await getLocalDb();
    return (await db.getAll(name)) as unknown as Row<T>[];
  }

  async rawPut<T extends TableName>(name: T, row: Row<T>): Promise<void> {
    const db = await getLocalDb();
    await db.put(name, row as unknown as Record<string, unknown> & { id: string });
  }

  async rawDelete<T extends TableName>(name: T, id: string): Promise<void> {
    const db = await getLocalDb();
    await db.delete(name, id);
  }

  async getAccessSnapshot(_companyId?: string | null) {
    const ctx = this.policyContext;
    return {
      companyId: ctx.companyId,
      membershipId: ctx.membershipId,
      isPlatformOwner: ctx.isPlatformOwner,
      isPlatformSupport: false,
      platformSupportSessionId: null,
      platformPermissions: [...(ctx.access.platformPermissions ?? [])],
      permissions: [...ctx.permissions],
      companyWide: ctx.scope?.companyWide ?? false,
      branchIds: [...(ctx.scope?.branchIds ?? [])],
      brandIds: [...(ctx.scope?.brandIds ?? [])],
      membershipIds: [...(ctx.scope?.membershipIds ?? [])],
      supervisedMembershipIds: ctx.access.membership?.supervisedMembershipIds ?? [],
    };
  }

  async recordAudit(input: {
    companyId: string | null;
    action: string;
    entityType: string;
    entityId: string | null;
    before?: unknown;
    after?: unknown;
  }): Promise<void> {
    await this.audit(input);
  }

  async audit(input: {
    companyId: string | null;
    action: string;
    entityType: string;
    entityId: string | null;
    before?: unknown;
    after?: unknown;
  }): Promise<void> {
    await this.rawPut('audit_logs', {
      id: newId(),
      companyId: input.companyId,
      actorUserId: this.policyContext.userId,
      actorMembershipId: this.policyContext.membershipId,
      action: input.action,
      entityType: input.entityType,
      entityId: input.entityId,
      before: redactForAudit(input.before ?? null),
      after: redactForAudit(input.after ?? null),
      severity: severityForAction(input.action),
      requestId: null,
      createdAt: new Date().toISOString(),
    });
  }

  now(): Date {
    return new Date();
  }

  /* -------------------------------- داخلي -------------------------------- */

  private core(): LocalCore {
    return {
      policy: () => this.policyContext,
      table: <T extends TableName>(name: T) => this.table(name),
      rawGet: <T extends TableName>(name: T, id: string) => this.rawGet(name, id),
      rawFind: <T extends TableName>(name: T, field: string, value: unknown) =>
        this.rawFind(name, field, value),
      rawAll: <T extends TableName>(name: T) => this.rawAll(name),
      rawPut: <T extends TableName>(name: T, row: Row<T>) => this.rawPut(name, row),
      rawDelete: <T extends TableName>(name: T, id: string) => this.rawDelete(name, id),
      blob: this.blob,
      audit: (input) => this.audit(input),
      now: () => this.now(),
      refresh: () => this.refresh(),
      auth: this.auth,
    };
  }
}

/** تُستخدم في الاختبارات لتفريغ كل البيانات المحلية. */
export async function resetLocalDatabase(): Promise<void> {
  await clearAllLocalData();
}

export type { QueryOptions };
